/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  IdentityProviderConnectionActiveKey,
  IdentityProviderConnectionActiveKey$inboundSchema,
} from "./identityproviderconnectionactivekey.js";
import {
  IdentityProviderConnectionApplicationsSync,
  IdentityProviderConnectionApplicationsSync$inboundSchema,
} from "./identityproviderconnectionapplicationssync.js";
import {
  IdentityProviderConnectionChecklistItem,
  IdentityProviderConnectionChecklistItem$inboundSchema,
} from "./identityproviderconnectionchecklistitem.js";

/**
 * Why the last verification did not complete: Okta rejected the credential, or could not be reached. Omitted when it completed.
 */
export const LastError = {
  CredentialRejected: "credential_rejected",
  OktaUnreachable: "okta_unreachable",
} as const;
/**
 * Why the last verification did not complete: Okta rejected the credential, or could not be reached. Omitted when it completed.
 */
export type LastError = OpenEnum<typeof LastError>;

/**
 * Which checklist template applies: custom_app when the admin creates the API Services app by hand, oin when the Speakeasy OIN listing is added from the catalog.
 */
export const OktaIdentityProviderConnectionListingMode = {
  CustomApp: "custom_app",
  Oin: "oin",
} as const;
/**
 * Which checklist template applies: custom_app when the admin creates the API Services app by hand, oin when the Speakeasy OIN listing is added from the catalog.
 */
export type OktaIdentityProviderConnectionListingMode = OpenEnum<
  typeof OktaIdentityProviderConnectionListingMode
>;

/**
 * Identity provider; always okta.
 */
export const OktaIdentityProviderConnectionProvider = {
  Okta: "okta",
} as const;
/**
 * Identity provider; always okta.
 */
export type OktaIdentityProviderConnectionProvider = OpenEnum<
  typeof OktaIdentityProviderConnectionProvider
>;

/**
 * Connection state. pending until the client ID is submitted and verified; verified when every required scope is granted over DPoP; degraded when verification found gaps (see verification_reasons); revoked once the credential was withdrawn.
 */
export const OktaIdentityProviderConnectionStatus = {
  Pending: "pending",
  Verified: "verified",
  Degraded: "degraded",
  Revoked: "revoked",
} as const;
/**
 * Connection state. pending until the client ID is submitted and verified; verified when every required scope is granted over DPoP; degraded when verification found gaps (see verification_reasons); revoked once the credential was withdrawn.
 */
export type OktaIdentityProviderConnectionStatus = OpenEnum<
  typeof OktaIdentityProviderConnectionStatus
>;

export const VerificationReasons = {
  MissingScope: "missing_scope",
  MissingRole: "missing_role",
  DpopNotBound: "dpop_not_bound",
  KeyNotFetched: "key_not_fetched",
  SecretRejected: "secret_rejected",
  ReadFailedOktaAppsRead: "read_failed:okta.apps.read",
  ReadFailedOktaUsersRead: "read_failed:okta.users.read",
  ReadFailedOktaGroupsRead: "read_failed:okta.groups.read",
} as const;
export type VerificationReasons = OpenEnum<typeof VerificationReasons>;

/**
 * An organization's Okta connection: the service application Speakeasy authenticates to the Okta Management API with, its verification state, and the console checklist. Never carries key material or tokens.
 */
export type OktaIdentityProviderConnection = {
  /**
   * The published signing key the identity provider currently trusts. Omitted after revocation.
   */
  activeKey?: IdentityProviderConnectionActiveKey | undefined;
  /**
   * Admin-entered Okta application ID the AI agent is bound to. Display only.
   */
  agentAppId?: string | undefined;
  /**
   * Admin-entered Okta AI agent ID. Display only.
   */
  agentId?: string | undefined;
  /**
   * Watermarks of the scheduled applications snapshot. The snapshot is reconciled on a fixed platform-wide schedule.
   */
  applicationsSync: IdentityProviderConnectionApplicationsSync;
  /**
   * Console steps for the connection's listing mode, in order.
   */
  checklist: Array<IdentityProviderConnectionChecklistItem>;
  /**
   * Okta application client ID. Omitted until submitted.
   */
  clientId?: string | undefined;
  /**
   * Whether the real Okta client ID has replaced the provisioning placeholder.
   */
  clientIdSubmitted: boolean;
  createdAt: Date;
  /**
   * Whether Okta issued a DPoP-bound token at the last verification.
   */
  dpopRequired: boolean;
  /**
   * Scopes Okta granted at the last verification.
   */
  grantedScopes: Array<string>;
  /**
   * Connection ID.
   */
  id: string;
  /**
   * Discovered authorization server issuer. Equal to org_url by construction.
   */
  issuerUrl: string;
  /**
   * Public JWKS URL the Okta app is configured to trust for private_key_jwt. Omitted for connections installed from the Okta Integration Network, which authenticate with a client secret.
   */
  jwksUrl?: string | undefined;
  /**
   * Why the last verification did not complete: Okta rejected the credential, or could not be reached. Omitted when it completed.
   */
  lastError?: LastError | undefined;
  /**
   * ISO 8601 timestamp of the last verification that found every required scope granted. Omitted until then.
   */
  lastVerifiedAt?: Date | undefined;
  /**
   * Which checklist template applies: custom_app when the admin creates the API Services app by hand, oin when the Speakeasy OIN listing is added from the catalog.
   */
  listingMode: OktaIdentityProviderConnectionListingMode;
  /**
   * Required scopes Okta did not grant at the last verification.
   */
  missingScopes: Array<string>;
  /**
   * Okta org URL, for example https://example.okta.com.
   */
  orgUrl: string;
  /**
   * Organization the connection belongs to.
   */
  organizationId: string;
  /**
   * Identity provider; always okta.
   */
  provider: OktaIdentityProviderConnectionProvider;
  /**
   * Organization remote session issuer that backs this connection. Omitted until the managed client is provisioned.
   */
  remoteSessionIssuerId?: string | undefined;
  /**
   * Okta API scopes the integration needs.
   */
  requiredScopes: Array<string>;
  /**
   * Connection state. pending until the client ID is submitted and verified; verified when every required scope is granted over DPoP; degraded when verification found gaps (see verification_reasons); revoked once the credential was withdrawn.
   */
  status: OktaIdentityProviderConnectionStatus;
  updatedAt: Date;
  /**
   * Typed reasons recorded by the last verification; empty when verified or not yet verified. missing_role is reserved for a later release.
   */
  verificationReasons: Array<VerificationReasons>;
};

/** @internal */
export const LastError$inboundSchema: z.ZodMiniType<LastError, unknown> =
  openEnums.inboundSchema(LastError);

/** @internal */
export const OktaIdentityProviderConnectionListingMode$inboundSchema:
  z.ZodMiniType<OktaIdentityProviderConnectionListingMode, unknown> = openEnums
    .inboundSchema(OktaIdentityProviderConnectionListingMode);

/** @internal */
export const OktaIdentityProviderConnectionProvider$inboundSchema:
  z.ZodMiniType<OktaIdentityProviderConnectionProvider, unknown> = openEnums
    .inboundSchema(OktaIdentityProviderConnectionProvider);

/** @internal */
export const OktaIdentityProviderConnectionStatus$inboundSchema: z.ZodMiniType<
  OktaIdentityProviderConnectionStatus,
  unknown
> = openEnums.inboundSchema(OktaIdentityProviderConnectionStatus);

/** @internal */
export const VerificationReasons$inboundSchema: z.ZodMiniType<
  VerificationReasons,
  unknown
> = openEnums.inboundSchema(VerificationReasons);

/** @internal */
export const OktaIdentityProviderConnection$inboundSchema: z.ZodMiniType<
  OktaIdentityProviderConnection,
  unknown
> = z.pipe(
  z.object({
    active_key: z.optional(IdentityProviderConnectionActiveKey$inboundSchema),
    agent_app_id: z.optional(z.string()),
    agent_id: z.optional(z.string()),
    applications_sync: IdentityProviderConnectionApplicationsSync$inboundSchema,
    checklist: z.array(IdentityProviderConnectionChecklistItem$inboundSchema),
    client_id: z.optional(z.string()),
    client_id_submitted: z.boolean(),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    dpop_required: z.boolean(),
    granted_scopes: z.array(z.string()),
    id: z.string(),
    issuer_url: z.string(),
    jwks_url: z.optional(z.string()),
    last_error: z.optional(LastError$inboundSchema),
    last_verified_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    listing_mode: OktaIdentityProviderConnectionListingMode$inboundSchema,
    missing_scopes: z.array(z.string()),
    org_url: z.string(),
    organization_id: z.string(),
    provider: OktaIdentityProviderConnectionProvider$inboundSchema,
    remote_session_issuer_id: z.optional(z.string()),
    required_scopes: z.array(z.string()),
    status: OktaIdentityProviderConnectionStatus$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    verification_reasons: z.array(VerificationReasons$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "active_key": "activeKey",
      "agent_app_id": "agentAppId",
      "agent_id": "agentId",
      "applications_sync": "applicationsSync",
      "client_id": "clientId",
      "client_id_submitted": "clientIdSubmitted",
      "created_at": "createdAt",
      "dpop_required": "dpopRequired",
      "granted_scopes": "grantedScopes",
      "issuer_url": "issuerUrl",
      "jwks_url": "jwksUrl",
      "last_error": "lastError",
      "last_verified_at": "lastVerifiedAt",
      "listing_mode": "listingMode",
      "missing_scopes": "missingScopes",
      "org_url": "orgUrl",
      "organization_id": "organizationId",
      "remote_session_issuer_id": "remoteSessionIssuerId",
      "required_scopes": "requiredScopes",
      "updated_at": "updatedAt",
      "verification_reasons": "verificationReasons",
    });
  }),
);

export function oktaIdentityProviderConnectionFromJSON(
  jsonString: string,
): SafeParseResult<OktaIdentityProviderConnection, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => OktaIdentityProviderConnection$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'OktaIdentityProviderConnection' from JSON`,
  );
}
